import { brandingSchema, LOGO_SLOTS, type Branding, type LogoSlot as Slot } from '@hearth/shared';
import { createFileRoute } from '@tanstack/react-router';
import { Flame, LayoutGrid, Moon, Server, Sparkles, Sun } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { LogoSlot } from '@/components/customisation/logo-slot';
import { footerLinks } from '@/components/customisation/site-footer';
import {
  ControlSection,
  PreviewFrame,
  StudioPanes,
} from '@/components/customisation/theme-studio-panes';
import { FormField } from '@/components/form-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { displayName, pickLogo } from '@/lib/customisation/logos';
import { useSiteDraft } from '@/lib/customisation/site-draft';
import { publicSiteFromDraft } from '@/lib/customisation/site';
import { useAuthenticatedSession } from '@/lib/session';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/admin/customisation/branding')({ component: Page });

type TextKey = Exclude<keyof Branding, 'logos' | 'logoUsage'>;

const TEXT_FIELDS: {
  key: TextKey;
  label: string;
  description?: string;
  placeholder?: string;
  long?: boolean;
}[] = [
  {
    key: 'displayName',
    label: 'Display name',
    description: 'Shown beside the logo and in the browser tab. Blank uses the panel name.',
    placeholder: 'Acme Hosting',
  },
  { key: 'companyName', label: 'Company name', placeholder: 'Acme Hosting Ltd' },
  {
    key: 'panelName',
    label: 'Panel name',
    description: 'How the product is called in emails and messages, e.g. “Acme Panel”.',
  },
  { key: 'tagline', label: 'Tagline', placeholder: 'Game servers without the fuss' },
  {
    key: 'footerText',
    label: 'Footer text',
    description: 'A line under every page, e.g. a copyright notice.',
    long: true,
  },
];

const URL_FIELDS: { key: TextKey; label: string; placeholder: string }[] = [
  { key: 'supportUrl', label: 'Support', placeholder: 'https://support.example.com or mailto:…' },
  { key: 'docsUrl', label: 'Documentation', placeholder: 'https://docs.example.com' },
  { key: 'websiteUrl', label: 'Website', placeholder: 'https://example.com' },
];

const USAGE_OPTIONS: { value: Branding['logoUsage']['login']; label: string }[] = [
  { value: 'auto', label: 'Automatic (by theme)' },
  { value: 'primary', label: 'Primary logo' },
  { value: 'light', label: 'Light-mode logo' },
  { value: 'dark', label: 'Dark-mode logo' },
  { value: 'compact', label: 'Compact logo' },
  { value: 'login', label: 'Login page logo' },
];

/** The validation message for one branding text field, or undefined. */
function fieldError(key: TextKey, value: string): string | undefined {
  const result = brandingSchema.shape[key].safeParse(value);
  return result.success ? undefined : result.error.issues[0]?.message;
}

function Page() {
  const draft = useSiteDraft();
  const session = useAuthenticatedSession();
  const { branding } = draft.site;
  const tenant = draft.tenant;

  const setText = (key: TextKey, value: string) =>
    draft.update((s) => {
      s.branding[key] = value === '' ? null : value;
    }, `Change ${key}`);
  const setLogo = (slot: Slot, id: string | null) =>
    draft.update((s) => {
      s.branding.logos[slot] = id;
    }, `Change ${slot} logo`);

  const controls = (
    <>
      <ControlSection
        title="Logos"
        description="Each slot is optional. Where a slot is empty the next best logo is used, and finally the Hearth mark."
      >
        <div className="grid gap-5">
          {LOGO_SLOTS.map((slot) => (
            <LogoSlot
              key={slot}
              tenant={tenant}
              slot={slot}
              value={branding.logos[slot]}
              onChange={(id) => setLogo(slot, id)}
            />
          ))}
        </div>
      </ControlSection>

      <ControlSection
        title="Where each logo is used"
        description="Automatic picks the light or dark logo to match the theme, the compact mark when the sidebar is collapsed."
      >
        <div className="grid gap-4 sm:grid-cols-3">
          {(['sidebar', 'header', 'login'] as const).map((place) => (
            <FormField
              key={place}
              label={place === 'sidebar' ? 'Sidebar' : place === 'header' ? 'Header' : 'Login page'}
            >
              <Select
                value={branding.logoUsage[place]}
                onValueChange={(v) =>
                  draft.update((s) => {
                    s.branding.logoUsage[place] = v as never;
                  }, 'Change logo usage')
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USAGE_OPTIONS.filter((o) => place === 'login' || o.value !== 'login').map(
                    (o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </FormField>
          ))}
        </div>
      </ControlSection>

      <ControlSection title="Names and text">
        <div className="grid gap-4">
          {TEXT_FIELDS.map((f) => {
            const value = branding[f.key] ?? '';
            return (
              <FormField
                key={f.key}
                label={f.label}
                description={f.description}
                error={fieldError(f.key, value)}
              >
                {f.long ? (
                  <Textarea
                    value={value}
                    onChange={(e) => setText(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    rows={2}
                  />
                ) : (
                  <Input
                    value={value}
                    onChange={(e) => setText(f.key, e.target.value)}
                    placeholder={f.placeholder}
                  />
                )}
              </FormField>
            );
          })}
        </div>
      </ControlSection>

      <ControlSection
        title="Links"
        description="Shown in the footer of every customer page. Full http(s) URLs, or mailto: for support."
      >
        <div className="grid gap-4">
          {URL_FIELDS.map((f) => {
            const value = branding[f.key] ?? '';
            return (
              <FormField key={f.key} label={f.label} error={fieldError(f.key, value)}>
                <Input
                  type="url"
                  inputMode="url"
                  value={value}
                  onChange={(e) => setText(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  spellCheck={false}
                />
              </FormField>
            );
          })}
        </div>
      </ControlSection>
    </>
  );

  return (
    <StudioPanes
      title="Branding"
      description="Your logos, names and links. Changes save to the draft as you type; publish from the toolbar when they are ready."
      controls={controls}
      preview={<BrandingPreview panelName={session.panel.name} />}
    />
  );
}

/** The sidebar header, collapsed sidebar, browser tab and login header, as they would look. */
function BrandingPreview({ panelName }: { panelName: string }) {
  const draft = useSiteDraft();
  const [scheme, setScheme] = useState<'light' | 'dark'>('dark');
  const site = publicSiteFromDraft(draft.site);
  const name = displayName(site, panelName);
  const sidebar = pickLogo(site, scheme, 'sidebar');
  const compact = pickLogo(site, scheme, 'sidebar', { compact: true });
  const login = pickLogo(site, scheme, 'login');
  const links = footerLinks(site.branding);
  const mark = (
    <span className="bg-brand/12 text-brand ring-brand/25 grid size-8 shrink-0 place-items-center rounded-lg ring-1 ring-inset">
      <Flame className="size-4" aria-hidden />
    </span>
  );
  return (
    <div className="grid gap-4">
      <div className="flex justify-end">
        <div
          className="bg-muted inline-flex rounded-lg p-[3px]"
          role="group"
          aria-label="Preview theme"
        >
          {(['dark', 'light'] as const).map((s) => (
            <Button
              key={s}
              type="button"
              size="xs"
              variant={scheme === s ? 'outline' : 'ghost'}
              aria-pressed={scheme === s}
              onClick={() => setScheme(s)}
            >
              {s === 'dark' ? <Moon /> : <Sun />} {s === 'dark' ? 'Dark' : 'Light'}
            </Button>
          ))}
        </div>
      </div>
      <PreviewFrame>
        <div
          className={cn(
            'bg-background text-foreground grid gap-0',
            scheme === 'dark' ? 'dark' : 'light',
          )}
          inert
        >
          <div className="grid grid-cols-[auto_minmax(0,1fr)] sm:grid-cols-[auto_auto_minmax(0,1fr)]">
            <Panel label="Sidebar">
              <div className="bg-sidebar text-sidebar-foreground w-52 border-r">
                <div className="flex h-14 items-center gap-2.5 border-b px-4">
                  {sidebar ? (
                    <img src={sidebar} alt="" className="h-8 w-auto max-w-[160px] object-contain" />
                  ) : (
                    mark
                  )}
                  {(!sidebar || sidebar === site.logos.compact) && (
                    <span className="truncate text-[0.9375rem] font-semibold tracking-tight">
                      {name}
                    </span>
                  )}
                </div>
                <ul className="grid gap-0.5 p-3 text-sm">
                  {[
                    { label: 'Overview', icon: LayoutGrid, active: true },
                    { label: 'My Servers', icon: Server },
                    { label: 'Hearth AI', icon: Sparkles },
                  ].map((i) => (
                    <li
                      key={i.label}
                      className={cn(
                        'flex h-9 items-center gap-2.5 rounded-md px-2.5 font-medium',
                        i.active ? 'bg-primary/10' : 'text-muted-foreground',
                      )}
                    >
                      <i.icon className={cn('size-4', i.active ? 'text-brand' : 'text-subtle')} />
                      {i.label}
                    </li>
                  ))}
                </ul>
              </div>
            </Panel>
            <Panel label="Collapsed" className="hidden sm:grid">
              <div className="bg-sidebar flex h-full w-[60px] flex-col items-center border-r">
                <div className="flex h-14 w-full items-center justify-center border-b">
                  {compact ? <img src={compact} alt="" className="size-8 object-contain" /> : mark}
                </div>
                <div className="grid gap-2 py-4">
                  {[LayoutGrid, Server, Sparkles].map((Icon, i) => (
                    <span
                      key={i}
                      className={cn(
                        'grid size-9 place-items-center rounded-md',
                        i === 0 ? 'bg-primary/10 text-brand' : 'text-subtle',
                      )}
                    >
                      <Icon className="size-4" />
                    </span>
                  ))}
                </div>
              </div>
            </Panel>
            <Panel label="Browser tab">
              <div className="grid gap-3 p-4">
                <div className="bg-elevated flex h-9 w-full max-w-[16rem] items-center gap-2 rounded-t-lg border border-b-0 px-3 text-xs">
                  {site.logos.favicon ? (
                    <img src={site.logos.favicon} alt="" className="size-4 object-contain" />
                  ) : (
                    <Flame className="text-brand size-4" aria-hidden />
                  )}
                  <span className="truncate">{name}</span>
                </div>
                <p className="text-muted-foreground text-xs">
                  {site.branding.tagline?.trim() || 'No tagline yet.'}
                </p>
                {site.branding.companyName && (
                  <p className="text-xs">
                    <span className="text-muted-foreground">Company:</span>{' '}
                    {site.branding.companyName}
                  </p>
                )}
              </div>
            </Panel>
          </div>
          <Panel label="Login page header" className="border-t">
            <div className="flex flex-col items-center gap-3 px-6 py-8 text-center">
              {login ? (
                <img src={login} alt="" className="max-h-14 w-auto max-w-[220px] object-contain" />
              ) : (
                <span className="bg-card text-brand shadow-card grid size-11 place-items-center rounded-xl border">
                  <Flame className="size-5" aria-hidden />
                </span>
              )}
              <p className="text-lg font-semibold tracking-tight">
                {site.login.heading?.trim() || name}
              </p>
              <div className="bg-card h-16 w-full max-w-xs rounded-xl border" />
            </div>
          </Panel>
          <Panel label="Footer" className="border-t">
            <div className="text-muted-foreground flex flex-wrap items-center gap-x-5 gap-y-1 px-4 py-3 text-xs">
              <span className="flex-1 basis-40">
                {site.branding.footerText?.trim() || <span className="italic">No footer text</span>}
              </span>
              {links.map((l) => (
                <span key={l.label} className="inline-flex items-center gap-1.5">
                  <l.icon className="size-3.5" /> {l.label}
                </span>
              ))}
            </div>
          </Panel>
        </div>
      </PreviewFrame>
    </div>
  );
}

function Panel({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('relative grid', className)}>
      <span className="text-subtle pointer-events-none absolute top-1 right-2 z-10 text-[0.625rem] font-semibold tracking-[0.08em] uppercase">
        {label}
      </span>
      {children}
    </div>
  );
}
